import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FolderOpen, Upload, FileText, Download, Trash2, Search, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { extractAndIndexDocument } from "@/lib/embeddings.functions";
import { logActivity } from "@/lib/activity.functions";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileLibrary } from "@/components/app/mobile/mobile-library";
import { Drawer, DrawerContent } from "@/components/ui/drawer";

export const Route = createFileRoute("/_authenticated/app/library")({
  component: LibraryPage,
});

type Doc = {
  id: string; name: string; area: string | null; size_bytes: number | null;
  created_at: string; storage_path: string | null; mime_type: string | null;
  tags: string[] | null; matter_id: string | null; indexed_at: string | null;
};

function LibraryPage() {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [filter, setFilter] = useState("");
  const [uploading, setUploading] = useState<string[]>([]);
  const [indexing, setIndexing] = useState<Set<string>>(new Set());
  const [userId, setUserId] = useState<string | null>(null);
  const indexFn = useServerFn(extractAndIndexDocument);
  const logFn = useServerFn(logActivity);

  const load = async () => {
    const { data } = await supabase.from("library_documents")
      .select("*").order("created_at", { ascending: false });
    setDocs((Array.isArray(data) ? data : []) as Doc[]);
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
    load();
  }, []);

  const reindex = async (docId: string, name: string) => {
    setIndexing((s) => new Set(s).add(docId));
    try {
      const r = await indexFn({ data: { documentId: docId } });
      if ((r as any).ok) {
        toast.success(t("pages.library.indexedToast", { name, n: (r as any).chunks }));
        await logFn({ data: { action: "library.index", entity: "library_document", entity_id: docId } }).catch(() => {});
        load();
      } else {
        toast.error((r as any).error ?? t("pages.library.indexFailed"));
      }
    } catch (e: any) {
      toast.error(e?.message ?? t("pages.common.error"));
    } finally {
      setIndexing((s) => { const n = new Set(s); n.delete(docId); return n; });
    }
  };

  const onDrop = useCallback(async (files: File[]) => {
    if (!userId) return toast.error(t("pages.library.invalidSession"));
    if (!Array.isArray(files) || files.length === 0) return;
    for (const file of files) {
      const fileId = crypto.randomUUID();
      setUploading((u) => [...u, file.name]);
      const path = `${userId}/orphan/${fileId}-${file.name}`;
      const { error: upErr } = await supabase.storage.from("library").upload(path, file, {
        cacheControl: "3600", upsert: false,
      });
      if (upErr) {
        toast.error(t("pages.library.uploadFailed", { name: file.name }));
        setUploading((u) => u.filter((n) => n !== file.name));
        continue;
      }
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      const area = guessArea(file.name);
      const { data: inserted, error: insErr } = await supabase.from("library_documents").insert({
        owner_id: userId, name: file.name, storage_path: path,
        size_bytes: file.size, mime_type: file.type, area, tags: [ext].filter(Boolean),
      }).select("id").single();
      if (insErr) {
        toast.error(insErr.message);
      } else {
        toast.success(t("pages.library.uploaded", { name: file.name }));
        if (inserted?.id) {
          await logFn({ data: { action: "library.upload", entity: "library_document", entity_id: inserted.id, metadata: { name: file.name } } }).catch(() => {});
          // auto-index in background
          indexFn({ data: { documentId: inserted.id } }).catch(() => {});
        }
      }
      setUploading((u) => u.filter((n) => n !== file.name));
    }
    load();
  }, [userId, indexFn, logFn]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: true, maxSize: 50 * 1024 * 1024,
  });

  const download = async (doc: Doc) => {
    if (!doc.storage_path) return;
    const { data, error } = await supabase.storage.from("library")
      .createSignedUrl(doc.storage_path, 300);
    if (error) return toast.error(error.message);
    window.open(data.signedUrl, "_blank");
  };

  const remove = async (doc: Doc) => {
    if (!confirm(t("pages.library.deleteConfirm", { name: doc.name }))) return;
    if (doc.storage_path) await supabase.storage.from("library").remove([doc.storage_path]);
    await supabase.from("library_documents").delete().eq("id", doc.id);
    await logFn({ data: { action: "library.delete", entity: "library_document", entity_id: doc.id, metadata: { name: doc.name } } }).catch(() => {});
    toast.success(t("pages.common.deleted"));
    load();
  };

  const filtered = docs.filter((d) =>
    !filter || d.name.toLowerCase().includes(filter.toLowerCase()) ||
    d.tags?.some((t) => t.toLowerCase().includes(filter.toLowerCase()))
  );

  const [plan, setPlan] = useState<string>("starter");
  useEffect(() => {
    supabase.from("profiles").select("plan").maybeSingle().then(({ data }) => setPlan((data as any)?.plan ?? "starter"));
  }, []);
  const quota = plan === "studio" ? 10000 : plan === "pro" ? 1000 : 200;

  const isMobile = useIsMobile();
  const [actionDoc, setActionDoc] = useState<Doc | null>(null);

  if (isMobile) {
    return (
      <>
        <MobileLibrary
          docs={filtered}
          filter={filter}
          setFilter={setFilter}
          uploading={uploading}
          onDrop={(f) => onDrop(f)}
          onAction={(d) => setActionDoc(d)}
        />
        <Drawer open={!!actionDoc} onOpenChange={(o) => !o && setActionDoc(null)}>
          <DrawerContent className="mob-surface-0 border-0 pb-safe text-foreground">
            <div className="px-4 py-4 space-y-2">
              <p className="truncate text-[14px] font-medium text-foreground">{actionDoc?.name}</p>
              {/* Primary actions — max 3 */}
              <button onClick={() => { if (actionDoc) download(actionDoc); setActionDoc(null); }}
                className="mob-tap flex w-full items-center justify-center rounded-xl bg-primary py-3 text-[14px] font-semibold text-primary-foreground">{t("pages.library.openBtn") ?? "Apri"}</button>
              <button onClick={() => { if (actionDoc) download(actionDoc); setActionDoc(null); }}
                className="mob-tap flex w-full items-center justify-center rounded-xl border mob-hairline-strong py-3 text-[14px]">{t("pages.common.download") ?? "Scarica"}</button>
              <button onClick={() => { if (actionDoc) remove(actionDoc); setActionDoc(null); }}
                className="mob-tap flex w-full items-center justify-center rounded-xl border mob-hairline-strong py-3 text-[14px] text-destructive">{t("pages.common.delete") ?? "Elimina"}</button>
              {/* Secondary — collapsed under "Altro" */}
              <details className="group rounded-xl border mob-hairline-strong">
                <summary className="mob-tap flex cursor-pointer items-center justify-center py-3 text-[13px] text-foreground list-none [&::-webkit-details-marker]:hidden">{t("pages.common.more") ?? "Altro"}</summary>
                <div className="border-t mob-hairline px-3 py-2">
                  <button onClick={() => { if (actionDoc) reindex(actionDoc.id, actionDoc.name); setActionDoc(null); }}
                    className="mob-tap flex w-full items-center justify-center rounded-lg py-2.5 text-[14px]">{t("pages.library.indexBtn")}</button>
                </div>
              </details>
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t("pages.library.title")} description={t("pages.library.description", { n: docs.length, quota })} />
      <PageBody>
        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder={t("pages.library.searchPlaceholder")} className="pl-9" />
          </div>
          <span className="text-sm text-muted-foreground">{t("pages.library.docsCount", { n: filtered.length })}</span>
        </div>

        <div {...getRootProps()}
          className={`mb-6 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
            isDragActive ? "border-accent bg-accent-muted/30" : "border-border hover:border-accent/50"
          }`}>
          <input {...getInputProps()} />
          <Upload className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">
            {isDragActive ? t("pages.library.dropActive") : t("pages.library.dropIdle")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{t("pages.library.dropHint")}</p>
          {uploading.length > 0 && (
            <div className="mt-4 space-y-1">
              {uploading.map((n) => (
                <div key={n} className="inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1 text-xs">
                  <Loader2 className="h-3 w-3 animate-spin" /> {n}
                </div>
              ))}
            </div>
          )}
        </div>

        {filtered.length === 0 ? (
          <Card className="flex flex-col items-center justify-center p-16 text-center">
            <FolderOpen className="mb-4 h-10 w-10 text-muted-foreground" />
            <h3 className="font-display text-xl font-semibold">{t("pages.library.emptyTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("pages.library.emptyDesc")}</p>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((d) => (
              <Card key={d.id} className="group p-4 transition-shadow hover:shadow-md">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium" title={d.name}>{d.name}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {d.size_bytes ? `${(d.size_bytes / 1024).toFixed(0)} KB` : "-"}
                      {d.area && ` · ${d.area}`}
                    </div>
                    {d.tags && d.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {d.tags.slice(0, 3).map((t) => (
                          <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      {d.indexed_at ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> {t("pages.library.indexed")}
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">{t("pages.library.notIndexed")}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => reindex(d.id, d.name)} disabled={indexing.has(d.id)} title={t("pages.library.indexBtn")}>
                    {indexing.has(d.id) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => download(d)}>
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(d)}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </PageBody>
    </>
  );
}

function guessArea(name: string): string | null {
  const n = name.toLowerCase();
  if (/contratt|locazion|appalt/.test(n)) return "Contrattualistica";
  if (/sentenz|ordinanz|cassazione/.test(n)) return "Giurisprudenza";
  if (/atto|citazion|comparsa|ricorso/.test(n)) return "Atti";
  if (/parere|memoria/.test(n)) return "Pareri";
  return null;
}
